// hooks/useSignalMarkers.ts - VERSÃO CORRIGIDA
// ✅ CORREÇÃO: Ao editar, não sobrescreve GPS que foi movido manualmente no mapa

import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  CreateImageMarkerRequest,
  ProjectImage,
  VideoProject,
  videoProjectService
} from 'services/videoService';

interface SignData {
  id?: number;
  service_id: number;
  signal_device_id: number;
  signal_film_id: number;
  signal_support_type: string | null;
  signal_support_id: number | null;
  signal_regions_id: number | null; // ✅ NOVO
  signal_region_area: string | null; // ✅ NOVO
  comments: string | null;
  signal_type?: string;
  type?: string;
  signal_device?: any;
  signal_support?: any;
  signal_region?: any; // ✅ NOVO
  x: number;
  y: number;
  timestamp: number;
  frameNumber: number;
  project_image_id?: number;
  lat: number;
  long: number;
  adjusted_lat?: number;
  adjusted_lng?: number;
  horizontal_offset?: number;
  depth_offset?: number;
}

interface FormData {
  service_id: number;
  signal_type: string;
  signal_device_id: number;
  signal_film_id: number;
  signal_support_type: string;
  signal_support_id: number | null;
  signal_regions_id: number | null; // ✅ NOVO
  signal_region_area: string | null; // ✅ NOVO
  comments: string;
  x: number;
  y: number;
  timestamp: number;
  frameNumber: number;
  imageId?: number;
  lat: number;
  long: number;
}

export const useSignalMarkers = (
  project: VideoProject | null,
  currentImage: ProjectImage | null,
  images: ProjectImage[],
  imageViewerRef: RefObject<any> | null
) => {
  const [savedSigns, setSavedSigns] = useState<SignData[]>([]);
  const [visibleSigns, setVisibleSigns] = useState<SignData[]>([]);
  const [mapMarkers, setMapMarkers] = useState<any[]>([]);
  const [pendingAutoSelectId, setPendingAutoSelectId] = useState<number | null>(
    null
  );

  // Form state
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<FormData>({
    service_id: 0,
    signal_type: '',
    signal_device_id: 0,
    signal_film_id: 0,
    signal_support_type: '',
    signal_support_id: null,
    signal_regions_id: null, // ✅ NOVO
    signal_region_area: null, // ✅ NOVO
    comments: '',
    x: 0,
    y: 0,
    timestamp: 0,
    frameNumber: 0,
    lat: 0,
    long: 0
  });

  const [imageClickEnabled, setImageClickEnabled] = useState(true);
  const [clickedCoordinates, setClickedCoordinates] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [showMarker, setShowMarker] = useState(false);

  // Drag state (imagem)
  const [dragModeEnabled, setDragModeEnabled] = useState(false);
  const [draggingSignIndex, setDraggingSignIndex] = useState<number | null>(
    null
  );
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragCoordinates, setDragCoordinates] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Drag state (mapa)
  const [mapDragModeEnabled, setMapDragModeEnabled] = useState(false);
  const [draggingMapSignId, setDraggingMapSignId] = useState<number | null>(
    null
  );
  const [mapDragCoordinates, setMapDragCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  // Selection state
  const [selectedSignIndex, setSelectedSignIndex] = useState<number | null>(
    null
  );
  const [isBlinking, setIsBlinking] = useState(false);

  const navigationSourceRef = useRef<'marker' | 'map' | null>(null);

  const handleSelectSign = useCallback(
    (index: number | null, source: 'marker' | 'map' | null = null) => {
      console.log('🎯 Selecionando sinalização:', { index, source });
      setSelectedSignIndex(index);
      navigationSourceRef.current = source;

      if (index !== null) {
        setIsBlinking(true);
        setTimeout(() => setIsBlinking(false), 2000);

        const sign = savedSigns[index];
        if (sign && images.length > 0) {
          const imageIndex = images.findIndex(
            img => img.id === sign.project_image_id
          );

          if (imageIndex !== -1) {
            console.log(
              `[Select Sign] 🎯 Imagem encontrada no índice ${imageIndex}`
            );

            window.dispatchEvent(
              new CustomEvent('navigateToImage', {
                detail: { imageIndex, signIndex: index }
              })
            );
          } else {
            console.warn(
              `[Select Sign] ⚠️ Imagem não encontrada para project_image_id: ${sign.project_image_id}`
            );
          }
        }
      }
    },
    [savedSigns, images]
  );

  const reloadProjectMarkers = useCallback(async () => {
    if (!project) return;

    try {
      const markers = await videoProjectService.getProjectMarkers(project.id);
      console.log('📦 Markers carregados:', markers.length);

      const markersAsSignData: SignData[] = await Promise.all(
        markers.map(async marker => {
          let deviceData = marker.signal_device;
          if (!deviceData && marker.signal_device_id) {
            const devices = await videoProjectService.getSignalDevices();
            deviceData = devices.find(d => d.id === marker.signal_device_id);
          }

          let supportData = marker.signal_support;
          let supportType = marker.signal_support_type;

          if (!supportType && marker.signal_support_id) {
            console.log(
              `🔍 Buscando tipo do suporte ID ${marker.signal_support_id}...`
            );
            try {
              supportData = await videoProjectService.getSignalSupportById(
                marker.signal_support_id
              );

              if (supportData) {
                supportType = supportData.type;
                console.log(`✅ Tipo encontrado: ${supportType}`);
              }
            } catch (error) {
              console.error('❌ Erro ao buscar suporte:', error);
            }
          } else if (supportData && !supportType) {
            supportType = supportData.type;
          }

          return {
            id: marker.id,
            service_id: marker.service_id,
            signal_device_id: marker.signal_device_id,
            signal_film_id: marker.signal_film_id,
            signal_support_type: supportType || null,
            signal_support_id: marker.signal_support_id,
            signal_regions_id: marker.signal_regions_id, // ✅ NOVO
            signal_region_area: marker.signal_region_area, // ✅ NOVO
            comments: marker.comments || null,
            signal_device: deviceData,
            signal_support: supportData,
            signal_region: marker.signal_region, // ✅ NOVO
            x: marker.x_coordinate,
            y: marker.y_coordinate,
            timestamp: 0,
            frameNumber: 0,
            project_image_id: marker.project_image_id,
            lat: marker.lat,
            long: marker.long,
            adjusted_lat: marker.adjusted_lat,
            adjusted_lng: marker.adjusted_lng,
            horizontal_offset: marker.horizontal_offset,
            depth_offset: marker.depth_offset
          };
        })
      );

      setSavedSigns(markersAsSignData);
    } catch (error) {
      console.error('❌ Erro ao carregar markers:', error);
    }
  }, [project]);

  const updateMapMarkers = useCallback(
    (signs: SignData[]) => {
      const newMapMarkers: any[] = [];

      if (
        currentImage &&
        currentImage.lat !== null &&
        currentImage.long !== null
      ) {
        newMapMarkers.push({
          id: 'current_position',
          lat: currentImage.lat,
          lng: currentImage.long,
          title: `📍 Posição Atual: ${currentImage.filename}`,
          type: 'pin',
          color: '#dc3545'
        });
      }

      signs.forEach(sign => {
        const correspondingImage = images.find(
          img => img.id === sign.project_image_id
        );
        let markerLat = sign.adjusted_lat || sign.lat;
        let markerLng = sign.adjusted_lng || sign.long;

        if (!markerLat || !markerLng) {
          if (
            correspondingImage &&
            correspondingImage.lat !== null &&
            correspondingImage.long !== null
          ) {
            markerLat = correspondingImage.lat;
            markerLng = correspondingImage.long;
          }
        }

        if (markerLat && markerLng) {
          const symbolName = sign.signal_device?.symbol_name?.replace(
            /\.png$/i,
            ''
          );

          newMapMarkers.push({
            id: `sign_${sign.id}`,
            lat: markerLat,
            lng: markerLng,
            title: `${
              sign.signal_device?.symbol_name ||
              sign.signal_device?.name ||
              'Sinalização'
            }${sign.adjusted_lat ? ' (GPS Ajustado)' : ''}`,
            type: 'sign',
            symbolName: symbolName,
            serviceId: sign.service_id,
            icon: sign.signal_device?.code,
            color: '#ffc107',
            isOverlay: false
          });
        }
      });

      console.log('🧪 MARKERS CRIADOS:', {
        total: newMapMarkers.length,
        signs: newMapMarkers.filter(m => m.type === 'sign').length
      });

      setMapMarkers(newMapMarkers);
    },
    [currentImage, images]
  );

  useEffect(() => {
    if (!showFormModal) {
      setShowMarker(false);
      setClickedCoordinates(null);
    }
  }, [showFormModal]);

  useEffect(() => {
    if (project) {
      console.log('🔄 Carregando markers do projeto:', project.id);
      reloadProjectMarkers();
    }
  }, [project, reloadProjectMarkers]);

  useEffect(() => {
    if (savedSigns.length > 0 && currentImage) {
      const visible = savedSigns.filter(
        sign => sign.project_image_id === currentImage.id
      );
      console.log(
        `👁️ Sinais visíveis na imagem ${currentImage.id}:`,
        visible.length
      );
      setVisibleSigns(visible);
    } else {
      setVisibleSigns([]);
    }
  }, [currentImage, savedSigns]);

  useEffect(() => {
    if (savedSigns.length > 0) {
      updateMapMarkers(savedSigns);
    }
  }, [currentImage, savedSigns, updateMapMarkers]);

  const enableImageClick = useCallback(() => {
    setImageClickEnabled(true);
    setShowMarker(false);
    setClickedCoordinates(null);
  }, []);

  useEffect(() => {
    const handleImageViewerClick = (e: CustomEvent) => {
      const { event, currentImageIndex, gpsResult } = e.detail;

      if (!imageClickEnabled || !currentImage) {
        console.log('❌ Clique bloqueado:', {
          imageClickEnabled,
          hasImage: !!currentImage
        });
        return;
      }

      const imgElement = imageViewerRef?.current?.getImageElement?.();

      if (!imgElement) {
        console.error('❌ Não foi possível obter elemento da imagem');
        return;
      }

      const rect = imgElement.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const relativeX = x / rect.width;
      const relativeY = y / rect.height;

      console.log('✅ Clique registrado:', { x: relativeX, y: relativeY });

      setClickedCoordinates({ x: relativeX, y: relativeY });
      setShowMarker(true);

      let gpsLat = currentImage.lat || 0;
      let gpsLng = currentImage.long || 0;

      if (gpsResult && gpsResult.adjustedGPS) {
        (window as any).__clickGPSResult = {
          ...gpsResult,
          timestamp: Date.now()
        };
        gpsLat = gpsResult.adjustedGPS.lat;
        gpsLng = gpsResult.adjustedGPS.lng;
        console.log('📍 GPS Ajustado:', { lat: gpsLat, lng: gpsLng });
      } else {
        delete (window as any).__clickGPSResult;
        console.log('📍 GPS Original:', { lat: gpsLat, lng: gpsLng });
      }

      setFormData({
        service_id: 1,
        signal_type: '',
        signal_device_id: 0,
        signal_film_id: 1,
        signal_support_type: '',
        signal_support_id: null,
        signal_regions_id: null, // ✅ NOVO
        signal_region_area: null, // ✅ NOVO
        comments: '',
        x: relativeX,
        y: relativeY,
        timestamp: currentImageIndex,
        frameNumber: currentImageIndex,
        imageId: currentImage.id,
        lat: gpsLat,
        long: gpsLng
      });

      setTimeout(() => {
        setShowFormModal(true);
      }, 50);
    };

    window.addEventListener(
      'imageViewerClick',
      handleImageViewerClick as EventListener
    );

    return () => {
      window.removeEventListener(
        'imageViewerClick',
        handleImageViewerClick as EventListener
      );
    };
  }, [
    imageClickEnabled,
    imageViewerRef,
    currentImage,
    setFormData,
    setShowFormModal
  ]);

  const handleFormSubmit = useCallback(
    async (data: FormData) => {
      if (!currentImage || !project) return;

      console.log('🚀 [SUBMIT] Iniciando salvamento...');

      try {
        let savedSignId: number | undefined;

        if (editingIndex !== null && savedSigns[editingIndex]?.id) {
          // MODO EDIÇÃO
          const existingSign = savedSigns[editingIndex];
          savedSignId = existingSign.id;

          console.log('✏️ [SUBMIT] MODO EDIÇÃO - ID:', savedSignId);

          const updatePayload: Partial<CreateImageMarkerRequest> = {
            service_id: data.service_id,
            signal_device_id: data.signal_device_id,
            signal_film_id: data.signal_film_id,
            signal_support_type: data.signal_support_type || null,
            signal_support_id: data.signal_support_id,
            signal_regions_id: data.signal_regions_id,
            signal_region_area: data.signal_region_area,
            comments: data.comments || '',
            x_coordinate: data.x,
            y_coordinate: data.y,
            type: data.signal_type
          };

          await videoProjectService.updateImageMarker(
            existingSign.id!,
            updatePayload as any
          );

          console.log('✅ [SUBMIT] Marker atualizado');

          // ✅ No modo edição, mantém a seleção atual
          setPendingAutoSelectId(savedSignId ?? null); // ✅ CORREÇÃO AQUI
        } else {
          // MODO CRIAÇÃO
          console.log('➕ [SUBMIT] MODO CRIAÇÃO');

          const clickResult = (window as any).__clickGPSResult;
          let finalLat = currentImage.lat || 0;
          let finalLng = currentImage.long || 0;
          let adjustedLat: number | undefined = undefined;
          let adjustedLng: number | undefined = undefined;
          let horizontalOffset: number | undefined = undefined;
          let depthOffset: number | undefined = undefined;

          if (clickResult && clickResult.adjustedGPS) {
            finalLat = clickResult.adjustedGPS.lat;
            finalLng = clickResult.adjustedGPS.lng;
            adjustedLat = clickResult.adjustedGPS.lat;
            adjustedLng = clickResult.adjustedGPS.lng;
            horizontalOffset = clickResult.horizontalOffset;
            depthOffset = clickResult.depthOffset;
            delete (window as any).__clickGPSResult;
          }

          const createPayload: CreateImageMarkerRequest = {
            project_image_id: currentImage.id,
            service_id: data.service_id,
            signal_device_id: data.signal_device_id,
            signal_film_id: data.signal_film_id,
            signal_support_type: data.signal_support_type || null,
            signal_support_id: data.signal_support_id,
            signal_regions_id: data.signal_regions_id,
            signal_region_area: data.signal_region_area,
            comments: data.comments || '',
            x_coordinate: data.x,
            y_coordinate: data.y,
            lat: finalLat,
            long: finalLng,
            type: data.signal_type,
            adjusted_lat: adjustedLat,
            adjusted_lng: adjustedLng,
            horizontal_offset: horizontalOffset,
            depth_offset: depthOffset
          };

          console.log('📤 [SUBMIT] Criando marker:', createPayload);

          const response = await videoProjectService.createImageMarker(
            createPayload
          );
          savedSignId = response.id;

          console.log('✅ [SUBMIT] Marker criado com ID:', savedSignId);

          // ✅ Marcar para auto-seleção após reload
          setPendingAutoSelectId(savedSignId ?? null); // ✅ CORREÇÃO AQUI
        }

        // Limpar modal
        console.log('🧹 [SUBMIT] Limpando estados do modal...');
        setShowFormModal(false);
        setEditingIndex(null);
        setFormData({
          service_id: 0,
          signal_type: '',
          signal_device_id: 0,
          signal_film_id: 0,
          signal_support_type: '',
          signal_support_id: null,
          signal_regions_id: null,
          signal_region_area: null,
          comments: '',
          x: 0,
          y: 0,
          timestamp: 0,
          frameNumber: 0,
          lat: 0,
          long: 0
        });

        // Reload markers
        console.log('🔄 [SUBMIT] Recarregando markers...');
        await reloadProjectMarkers();

        console.log(
          '✅ [SUBMIT] Reload concluído - aguardando atualização do estado...'
        );
      } catch (error) {
        console.error('❌ [SUBMIT] Erro ao salvar marker:', error);
        setPendingAutoSelectId(null); // Limpar em caso de erro
        throw error;
      }
    },
    [
      currentImage,
      project,
      editingIndex,
      savedSigns,
      reloadProjectMarkers,
      setShowFormModal,
      setEditingIndex,
      setFormData
    ]
  );
  // ✅ NOVO: useEffect para escutar evento de auto-seleção
  useEffect(() => {
    if (pendingAutoSelectId === null) return;

    console.log('🔍 [AUTO-SELECT] Verificando se sign foi carregado...', {
      pendingId: pendingAutoSelectId,
      totalSigns: savedSigns.length,
      signIds: savedSigns.map(s => s.id).slice(-5) // Últimos 5 IDs
    });

    const signIndex = savedSigns.findIndex(s => s.id === pendingAutoSelectId);

    if (signIndex !== -1) {
      console.log('✅ [AUTO-SELECT] Sign encontrado no índice:', signIndex);

      // Pequeno delay para garantir renderização
      setTimeout(() => {
        handleSelectSign(signIndex, 'marker');
        setPendingAutoSelectId(null); // Limpar após selecionar
        console.log('🎯 [AUTO-SELECT] Seleção aplicada com sucesso!');
      }, 100);
    } else {
      console.warn('⚠️ [AUTO-SELECT] Sign ainda não está no estado atualizado');
    }
  }, [savedSigns, pendingAutoSelectId, handleSelectSign]);

  const handleEditSign = useCallback(
    (index: number) => {
      handleSelectSign(index);

      const sign = savedSigns[index];
      if (!sign) return;

      let signalType = sign.type || sign.signal_type || '';
      if (!signalType && sign.signal_device) {
        signalType = sign.signal_device.type;
      }

      console.log('✏️ Editando sinalização:', {
        index,
        id: sign.id,
        gps: { lat: sign.lat, long: sign.long },
        adjusted: { lat: sign.adjusted_lat, lng: sign.adjusted_lng }
      });

      // ✅ GPS no formData é apenas para referência visual
      // Não será usado no save quando em modo edição
      setFormData({
        service_id: sign.service_id,
        signal_type: signalType,
        signal_device_id: sign.signal_device_id,
        signal_film_id: sign.signal_film_id,
        signal_support_type: sign.signal_support_type || '',
        signal_support_id: sign.signal_support_id,
        signal_regions_id: sign.signal_regions_id || null, // ✅ NOVO
        signal_region_area: sign.signal_region_area || null, // ✅ NOVO
        comments: sign.comments || '',
        x: sign.x,
        y: sign.y,
        timestamp: sign.timestamp,
        frameNumber: sign.frameNumber,
        imageId: sign.project_image_id,
        lat: sign.lat,
        long: sign.long
      });

      setEditingIndex(index);
      setShowFormModal(true);
    },
    [savedSigns, handleSelectSign]
  );

  const handleDeleteSign = useCallback(
    async (index: number) => {
      const sign = savedSigns[index];
      if (!sign?.id) return;

      if (
        !window.confirm(
          `Deseja realmente excluir esta sinalização?\n\nCódigo: ${
            sign.signal_device?.code || 'N/A'
          }\nTipo: ${sign.signal_device?.name || 'N/A'}`
        )
      ) {
        return;
      }

      try {
        await videoProjectService.deleteImageMarker(sign.id);
        console.log('✅ Marker deletado');
        await reloadProjectMarkers();
      } catch (error) {
        console.error('❌ Erro ao deletar marker:', error);
        await reloadProjectMarkers();
      }
    },
    [savedSigns, reloadProjectMarkers]
  );

  const handleMarkerMouseDown = useCallback(
    (e: React.MouseEvent, index: number, sign: SignData) => {
      if (!dragModeEnabled) return;

      e.preventDefault();
      e.stopPropagation();

      let imgElement: HTMLImageElement | null = null;

      if (imageViewerRef?.current?.getImageElement) {
        imgElement = imageViewerRef.current.getImageElement();
      }

      if (!imgElement) {
        console.error('❌ Não foi possível obter elemento da imagem para drag');
        return;
      }

      const rect = imgElement.getBoundingClientRect();
      const markerX = sign.x * rect.width;
      const markerY = sign.y * rect.height;
      const offsetX = e.clientX - rect.left - markerX;
      const offsetY = e.clientY - rect.top - markerY;

      console.log('🖱️ Iniciando drag:', { index, offsetX, offsetY });

      setDraggingSignIndex(index);
      setDragOffset({ x: offsetX, y: offsetY });
    },
    [dragModeEnabled, imageViewerRef]
  );

  const handleMarkerMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (draggingSignIndex === null) return;

      e.preventDefault();
      e.stopPropagation();

      let imgElement: HTMLImageElement | null = null;

      if (imageViewerRef?.current?.getImageElement) {
        imgElement = imageViewerRef.current.getImageElement();
      }

      if (!imgElement) return;

      const rect = imgElement.getBoundingClientRect();
      const x = e.clientX - rect.left - dragOffset.x;
      const y = e.clientY - rect.top - dragOffset.y;
      const relativeX = Math.max(0, Math.min(1, x / rect.width));
      const relativeY = Math.max(0, Math.min(1, y / rect.height));

      console.log('🖱️ Movendo:', { relativeX, relativeY });

      setDragCoordinates({ x: relativeX, y: relativeY });

      const updatedSigns = [...savedSigns];
      updatedSigns[draggingSignIndex] = {
        ...updatedSigns[draggingSignIndex],
        x: relativeX,
        y: relativeY
      };
      setSavedSigns(updatedSigns);
    },
    [draggingSignIndex, dragOffset, savedSigns, imageViewerRef]
  );

  const handleMarkerMouseUp = useCallback(async () => {
    if (draggingSignIndex === null) return;

    console.log('🖱️ Finalizando drag:', draggingSignIndex);

    const sign = savedSigns[draggingSignIndex];

    setDraggingSignIndex(null);
    setDragOffset({ x: 0, y: 0 });
    setDragCoordinates(null);

    if (sign?.id) {
      try {
        await videoProjectService.updateImageMarker(sign.id, {
          x_coordinate: sign.x,
          y_coordinate: sign.y
        } as any);
        console.log('✅ Posição atualizada:', { x: sign.x, y: sign.y });
        await reloadProjectMarkers();
      } catch (error) {
        console.error('❌ Erro ao atualizar posição:', error);
        await reloadProjectMarkers();
      }
    }
  }, [draggingSignIndex, savedSigns, reloadProjectMarkers]);

  useEffect(() => {
    if (draggingSignIndex === null) return;

    const handleGlobalMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      console.log('🖱️ Global mouseup detectado');
      handleMarkerMouseUp();
    };

    const handleGlobalMouseLeave = () => {
      console.log('🖱️ Mouse saiu da janela');
      handleMarkerMouseUp();
    };

    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('mouseleave', handleGlobalMouseLeave);

    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('mouseleave', handleGlobalMouseLeave);
    };
  }, [draggingSignIndex, handleMarkerMouseUp]);

  useEffect(() => {
    if (draggingSignIndex !== null) {
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [draggingSignIndex]);

  // Handlers para drag no mapa
  const handleMapSignDragStart = useCallback(
    (signId: number, lat: number, lng: number) => {
      if (!mapDragModeEnabled) return;
      console.log('🗺️ Iniciando drag no mapa:', { signId, lat, lng });
      setDraggingMapSignId(signId);
      setMapDragCoordinates({ lat, lng });
    },
    [mapDragModeEnabled]
  );

  const handleMapSignDragMove = useCallback(
    (lat: number, lng: number) => {
      if (draggingMapSignId === null) return;
      setMapDragCoordinates({ lat, lng });
    },
    [draggingMapSignId]
  );

  const handleMapSignDragEnd = useCallback(
    async (lat: number, lng: number) => {
      if (draggingMapSignId === null) return;

      console.log('🗺️ Finalizando drag no mapa:', {
        signId: draggingMapSignId,
        lat,
        lng
      });

      const signIndex = savedSigns.findIndex(s => s.id === draggingMapSignId);

      if (signIndex === -1) {
        console.error('❌ Sign não encontrado');
        setDraggingMapSignId(null);
        setMapDragCoordinates(null);
        return;
      }

      const sign = savedSigns[signIndex];

      // Atualizar localmente
      setSavedSigns(prevSigns =>
        prevSigns.map(s =>
          s.id === draggingMapSignId
            ? {
                ...s,
                lat: lat,
                long: lng,
                adjusted_lat: lat,
                adjusted_lng: lng
              }
            : s
        )
      );

      setDraggingMapSignId(null);
      setMapDragCoordinates(null);

      // Salvar no backend
      try {
        await videoProjectService.updateImageMarker(sign.id!, {
          lat: lat,
          long: lng
        } as any);

        console.log('✅ GPS salvo no backend:', { lat, lng });
      } catch (error) {
        console.error('❌ Erro ao salvar GPS:', error);

        // Reverter mudança local em caso de erro
        setSavedSigns(prevSigns =>
          prevSigns.map(s =>
            s.id === draggingMapSignId
              ? {
                  ...s,
                  lat: sign.lat,
                  long: sign.long,
                  adjusted_lat: sign.adjusted_lat,
                  adjusted_lng: sign.adjusted_lng
                }
              : s
          )
        );

        alert('Erro ao salvar nova posição. A mudança foi revertida.');
      }
    },
    [draggingMapSignId, savedSigns]
  );

  return {
    savedSigns,
    visibleSigns,
    mapMarkers,
    showFormModal,
    editingIndex,
    formData,
    imageClickEnabled,
    videoClickEnabled: imageClickEnabled,
    dragModeEnabled,
    clickedCoordinates,
    showMarker,
    draggingSignIndex,
    dragCoordinates,
    mapDragModeEnabled,
    draggingMapSignId,
    mapDragCoordinates,
    selectedSignIndex,
    isBlinking,
    setShowFormModal,
    setEditingIndex,
    setFormData,
    setImageClickEnabled,
    setVideoClickEnabled: setImageClickEnabled,
    setDragModeEnabled,
    setMapDragModeEnabled,
    setClickedCoordinates,
    setShowMarker,
    enableImageClick,
    enableVideoClick: enableImageClick,
    handleFormSubmit,
    handleEditSign,
    handleDeleteSign,
    handleSelectSign,
    handleMarkerMouseDown,
    handleMarkerMouseMove,
    handleMarkerMouseUp,
    handleMapSignDragStart,
    handleMapSignDragMove,
    handleMapSignDragEnd,
    reloadProjectMarkers,
    updateMapMarkers
  };
};
